import React, { useCallback, useEffect, useState } from "react";
import { OadsSidebar } from "@oppizi-ecosystem/oads-sidebar";
// eslint-disable-next-line import/no-unresolved -- exports-map css subpath the resolver can't follow
import "@oppizi-ecosystem/oads-sidebar/styles.css";
import { useCookieState } from "cookie-state";
import { useTheme } from "campaign-builder/theme";
import { EDDMLogo } from "components/icons/components/EDDMlogo";
import { EDDMLogoDark } from "components/icons/components/EDDMlogoDark";
import { sharedCookieDomain } from "campaign-builder/sharedCookieDomain";
import { subscribeSidebarOpen } from "campaign-builder/sidebarOpenBus";

type ThemedOadsSidebarProps = Omit<React.ComponentProps<typeof OadsSidebar>, "theme" | "logo"> & {
  /** EDDM-only client: render the EDDM mark, in the variant matching the active theme. */
  eddmLogo?: boolean;
  /**
   * Land collapsed every time this page mounts, whatever the shared cookie says.
   * The cookie is left alone until the user toggles the menu here, so other
   * pages / apps keep their own preference.
   */
  collapseOnMount?: boolean;
};

/**
 * Leaf theme subscriber: keeps the sidebar in sync with the toggle without
 * subscribing the whole builder tree to theme changes (a toggle re-renders
 * only this component). Also carries the open/collapsed state, shared across
 * apps via a cookie (PAD-31716) so it persists and stays in sync with
 * platform-next. A caller may still override `open`/`onOpenChange` via props.
 */
export const ThemedOadsSidebar = ({ eddmLogo, collapseOnMount = false, ...props }: ThemedOadsSidebarProps) => {
  const { value: cookieOpen, setValue: setOpen } = useCookieState<boolean>("sidebar_open", true, {
    domain: sharedCookieDomain(),
    sameSite: "lax",
    days: 365,
  });
  // Requests from elsewhere on the page (the Copilot chat collapses the menu
  // when it opens). Writing through setOpen keeps the shared cookie in sync.
  // Local override that wins over the cookie until the first toggle on this page.
  const [forcedClosed, setForcedClosed] = useState(collapseOnMount);
  const open = forcedClosed ? false : cookieOpen;
  const onOpenChange = useCallback(
    (next: boolean) => {
      setForcedClosed(false);
      setOpen(() => next);
    },
    [setOpen]
  );
  useEffect(() => subscribeSidebarOpen(onOpenChange), [onOpenChange]);
  const theme = useTheme();
  const eddmMark = theme === "dark" ? <EDDMLogoDark /> : <EDDMLogo />;
  const logo = eddmLogo ? eddmMark : undefined;
  return <OadsSidebar theme={theme} logo={logo} open={open} onOpenChange={onOpenChange} {...props} />;
};
